import { useState } from 'react';
import { Card, Button, Typography, Space, Result, message, theme } from 'antd';
import { MailOutlined, ReloadOutlined, SendOutlined, LogoutOutlined } from '@ant-design/icons';
import { keycloak } from './keycloak';
import apiClient from '../api/client';
import { usePermissions } from '../providers';
import { useT } from '../locales';

const { Text } = Typography;

/**
 * Shown while `/api/me` answers 403 USER_NOT_LINKED: the account is authenticated
 * in Keycloak but has no user row in aid. That is a normal state in a realm
 * shared with other apps, so the page states it plainly and offers the two exits
 * that actually work — re-check provisioning, and log out.
 *
 * "Volver al inicio" was removed on purpose: it navigated to `/`, where
 * ProtectedRoute rendered this very page again, so it was a loop with no exit.
 */
export function UserNotLinkedPage() {
  const t = useT();
  const { unlinkedPayload, refetch } = usePermissions();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const { token } = theme.useToken();

  const email = unlinkedPayload?.email ?? '';
  const adminContact =
    unlinkedPayload?.admin_contact ?? import.meta.env.VITE_ADMIN_CONTACT_EMAIL ?? 'admin@aid.com';

  const handleRequestAccess = async () => {
    setSending(true);
    setRequestFailed(false);
    try {
      await apiClient.post('/me/request-access');
      setSent(true);
      message.success(t('noAccess.requestSent'));
    } catch {
      // POST /api/me/request-access sits behind JwtAuthGuard, so it rejects for
      // exactly the accounts that reach this page. Say that instead of a generic
      // error, and point at the channel that does work.
      setRequestFailed(true);
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    keycloak.logout({ redirectUri: window.location.origin + '/login' });
  };

  const mailtoSubject = encodeURIComponent('Solicitud de acceso a aid');
  const mailtoString = encodeURIComponent(
    `Hola,\n\nSolicito acceso a la aplicación aid.\n\nMi email: ${email}\n\nSaludos.`,
  );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: token.colorBgLayout,
      }}
    >
      <Card style={{ maxWidth: 480, width: '100%', margin: 24 }}>
        <Result status="warning" title={t('noAccess.title')} subTitle={t('noAccess.description')} />

        {email && (
          <Space orientation="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Text strong>{t('noAccess.email')}:</Text>
            <Text>{email}</Text>
          </Space>
        )}

        {sent ? (
          <Text type="success">{t('noAccess.requestSent')}</Text>
        ) : (
          <Space orientation="vertical" style={{ width: '100%' }}>
            {requestFailed && <Text type="danger">{t('noAccess.requestFailed')}</Text>}

            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={handleRequestAccess}
              block
            >
              {t('noAccess.requestAccess')}
            </Button>

            <Button
              icon={<MailOutlined />}
              href={`mailto:${adminContact}?subject=${mailtoSubject}&body=${mailtoString}`}
              block
            >
              {t('noAccess.contactAdmin')}
            </Button>
          </Space>
        )}

        <Space orientation="vertical" style={{ width: '100%', marginTop: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} block>
            {t('noAccess.retry')}
          </Button>

          <Button icon={<LogoutOutlined />} onClick={handleLogout} block>
            {t('noAccess.logout')}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
